<template>
  <div class="video-uploader">
    <!-- Estado: Idle (Seleção de arquivo) -->
    <div v-if="uploadState === 'idle'" class="upload-idle">
      <input
        ref="fileInput"
        type="file"
        accept="video/*"
        @change="handleFileSelect"
        style="display: none"
      />

      <button class="btn-select-file" @click="$refs.fileInput.click()">
        <i class="fa-regular fa-folder-open"></i>
        Selecionar Vídeo
      </button>

      <div v-if="file" class="file-info">
        <div class="file-details">
          <i class="fa-regular fa-video"></i>
          <div>
            <p class="file-name">{{ file.name }}</p>
            <p class="file-size">{{ formatFileSize(file.size) }}</p>
          </div>
        </div>
        <button class="btn-upload" @click="handleUpload">
          <i class="fa-regular fa-arrow-up"></i>
          Iniciar Upload
        </button>
      </div>

      <div v-if="error" class="error-message">
        <i class="fa-regular fa-circle-exclamation"></i>
        {{ error }}
      </div>
    </div>

    <!-- Estado: Uploading (Upload em progresso) -->
    <div v-if="uploadState === 'uploading'" class="upload-progress">
      <h3>
        <i class="fa-regular fa-arrow-up-from-bracket"></i>
        Enviando Vídeo...
      </h3>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: `${uploadProgress}%` }"
        ></div>
      </div>
      <p class="progress-text">{{ uploadProgress }}%</p>
      <p class="info-text">{{ file ? file.name : "" }}</p>
      <button class="btn-cancel" @click="handleCancel">
        <i class="fa-regular fa-xmark"></i>
        Cancelar
      </button>
    </div>

    <!-- Estado: Success (Sucesso) -->
    <div v-if="uploadState === 'success'" class="upload-success">
      <div class="success-icon">
        <i class="fa-regular fa-circle-check"></i>
      </div>
      <h3>Vídeo Pronto!</h3>
      <p>Seu vídeo foi enviado e processado com sucesso.</p>

      <!-- Informações do Vídeo -->
      <div v-if="videoResult" class="video-info-card">
        <div class="video-info-header">
          <i class="fa-regular fa-video"></i>
          <strong>{{ videoResult.title }}</strong>
        </div>

        <div class="video-info-details">
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="badge badge-success">
              <i class="fa-regular fa-circle-check"></i>
              Pronto para uso
            </span>
          </div>

          <div v-if="videoResult.duration" class="info-row">
            <span class="info-label">Duração:</span>
            <span>{{ formatDuration(videoResult.duration) }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">URL do Vídeo:</span>
            <a :href="videoResult.videoUrl" target="_blank" class="video-link">
              <i class="fa-regular fa-link"></i>
              Ver vídeo
            </a>
          </div>

          <div class="info-row">
            <span class="info-label">Embed URL:</span>
            <button
              @click="copyToClipboard(videoResult.embedUrl)"
              class="btn-copy"
              title="Copiar URL"
            >
              <i class="fa-regular fa-copy"></i>
              Copiar
            </button>
          </div>
        </div>
      </div>

      <button class="btn-new-upload" @click="resetUploader">
        <i class="fa-regular fa-arrow-rotate-right"></i>
        Substituir Vídeo
      </button>
    </div>

    <!-- Estado: Error (Erro) -->
    <div v-if="uploadState === 'error'" class="upload-error">
      <div class="error-icon">
        <i class="fa-regular fa-circle-exclamation"></i>
      </div>
      <h3>Erro no Upload</h3>
      <p>{{ error }}</p>
      <div class="error-actions">
        <button class="btn-retry" @click="handleRetry">
          <i class="fa-regular fa-rotate-right"></i>
          Tentar Novamente
        </button>
        <button class="btn-back" @click="resetUploader">
          <i class="fa-regular fa-arrow-left"></i>
          Voltar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { VideoUploadService } from "@/services/videoUpload";
import { getToken } from "@/setup/api";

export default {
  name: "VideoUploader",
  props: {
    lessonId: {
      type: [String, Number],
      default: null
    },
    lessonTitle: {
      type: String,
      default: null
    },
    collectionId: {
      type: [String, Number],
      default: null
    },
    existingVideo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      file: null,
      uploadState: "idle", // idle, uploading, success, error
      uploadProgress: 0,
      error: null,
      videoId: null,
      uploadService: null,
      videoResult: null // Armazena informações do vídeo após sucesso
    };
  },
  mounted() {
    this.initUploadService();
    this.checkExistingVideo();
  },
  methods: {
    /**
     * Método público para abrir o seletor de arquivo
     * Pode ser chamado pelo componente pai via ref
     */
    openFileSelector() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    initUploadService() {
      const token = getToken();
      this.uploadService = new VideoUploadService(token);
    },

    checkExistingVideo() {
      // Se existe um vídeo já carregado, mostrar estado de sucesso
      if (this.existingVideo && this.existingVideo.status === "ready") {
        this.uploadState = "success";
        this.videoResult = {
          id: this.existingVideo.id,
          title: this.existingVideo.title,
          duration: this.existingVideo.duration,
          videoUrl: this.existingVideo.video_url,
          embedUrl: this.existingVideo.embed_url,
          bunnyVideoId: this.existingVideo.bunny_video_id
        };
      }
    },

    handleFileSelect(event) {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        const validation = this.uploadService.validateFile(selectedFile);

        if (!validation.valid) {
          this.error = validation.error;
          this.$message.error(validation.error);
          return;
        }

        this.file = selectedFile;
        this.error = null;
      }
    },

    async handleUpload() {
      if (!this.file) return;

      this.uploadState = "uploading";
      this.error = null;
      this.uploadProgress = 0;

      try {
        const result = await this.uploadService.uploadVideo(
          {
            lessonId: this.lessonId,
            title: this.lessonTitle || this.file.name.replace(/\.[^/.]+$/, ""), // Usa título da aula ou nome do arquivo
            file: this.file,
            collectionId: this.collectionId
          },
          {
            onUploadProgress: progress => {
              this.uploadProgress = progress.percentage;
            }
          }
        );

        // Upload concluído! Status será "uploading" ou "processing"
        // O webhook do Bunny atualizará quando estiver "ready"
        this.uploadState = "success";
        this.videoResult = result;
        this.videoId = result.id;

        this.$message.success(
          "✅ Vídeo enviado! A Looma Videos está processando."
        );

        this.$emit("success", result);
      } catch (err) {
        this.uploadState = "error";
        this.error = err.message;
        this.$message.error(err.message);
        this.$emit("error", err);
      }
    },

    async handleCancel() {
      if (!this.videoId) {
        this.resetUploader();
        return;
      }

      try {
        await this.uploadService.cancelUpload(this.videoId);
        this.$message.info("Upload cancelado");
        this.resetUploader();
      } catch (err) {
        this.error = err.message;
        this.$message.error(err.message);
      }
    },

    async handleRetry() {
      if (this.file) {
        await this.handleUpload();
      }
    },

    resetUploader() {
      this.uploadState = "idle";
      this.file = null;
      this.uploadProgress = 0;
      this.videoId = null;
      this.error = null;
      this.videoResult = null;

      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success("URL copiada para a área de transferência!");
      } catch (err) {
        this.$message.error("Erro ao copiar URL");
      }
    },

    formatDuration(seconds) {
      if (!seconds) return "0s";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m ${secs}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
      }
      return `${secs}s`;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    }
  }
};
</script>

<style lang="scss" scoped>
.video-uploader {
  max-width: 600px;
  margin: 0 auto;
  padding: 32px;
  border: 2px dashed var(--semantic-neutral-400, #939d9d);
  border-radius: 12px;
  text-align: center;
  background-color: var(--bw-1000, #fff);

  .btn-select-file,
  .btn-upload,
  .btn-cancel,
  .btn-retry,
  .btn-back,
  .btn-new-upload {
    padding: 12px 24px;
    margin: 8px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    i {
      font-size: 18px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .btn-select-file,
  .btn-upload,
  .btn-new-upload {
    background-color: var(--primary, #422648);
    color: white;

    &:hover:not(:disabled) {
      background-color: var(--primaryDark, #2e1a30);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  .btn-cancel,
  .btn-back {
    background-color: var(--semantic-neutral-400, #939d9d);
    color: white;

    &:hover {
      background-color: var(--semantic-neutral-500, #6c757d);
    }
  }

  .btn-retry {
    background-color: #ffc107;
    color: #000;

    &:hover {
      background-color: #ffb300;
    }
  }

  .file-info {
    margin: 24px 0;
    padding: 20px;
    background-color: var(--semantic-neutral-100, #f8f9fa);
    border-radius: 8px;

    .file-details {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-bottom: 16px;

      i {
        font-size: 32px;
        color: var(--primary, #422648);
      }

      .file-name {
        font-weight: 500;
        color: var(--semantic-neutral-800, #212529);
        margin: 0;
        word-break: break-word;
      }

      .file-size {
        color: var(--semantic-neutral-600, #6c757d);
        margin: 4px 0 0 0;
        font-size: 14px;
      }
    }
  }

  .progress-bar {
    width: 100%;
    height: 32px;
    background-color: var(--semantic-neutral-200, #e9ecef);
    border-radius: 16px;
    overflow: hidden;
    margin: 20px 0;
    position: relative;

    .progress-fill {
      height: 100%;
      background: linear-gradient(
        90deg,
        var(--primary, #422648),
        var(--secondary, #4d3d74)
      );
      transition: width 0.3s ease;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.3),
          transparent
        );
        animation: shimmer 2s infinite;
      }
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .progress-text {
    font-size: 20px;
    font-weight: 600;
    color: var(--primary, #422648);
    margin: 8px 0;
  }

  .info-text {
    color: var(--semantic-neutral-600, #6c757d);
    font-size: 14px;
    margin: 12px 0;
  }

  .error-message {
    color: #dc3545;
    padding: 16px;
    background-color: #f8d7da;
    border-radius: 8px;
    margin: 16px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    i {
      font-size: 20px;
    }
  }

  .upload-success {
    padding: 24px;

    .success-icon {
      i {
        font-size: 64px;
        color: #28a745;
      }
    }

    h3 {
      color: #28a745;
      font-size: 24px;
      margin: 16px 0;
    }

    p {
      color: var(--semantic-neutral-700, #495057);
      font-size: 16px;
      margin: 12px 0 24px 0;
    }

    .video-info-card {
      background-color: var(--semantic-neutral-100, #f8f9fa);
      border: 1px solid var(--semantic-neutral-300, #dee2e6);
      border-radius: 12px;
      padding: 20px;
      margin: 24px 0;
      text-align: left;

      .video-info-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid var(--semantic-neutral-300, #dee2e6);

        i {
          font-size: 24px;
          color: var(--primary, #422648);
        }

        strong {
          font-size: 18px;
          color: var(--semantic-neutral-800, #212529);
        }
      }

      .video-info-details {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;

          .info-label {
            font-weight: 500;
            color: var(--semantic-neutral-700, #495057);
            font-size: 14px;
          }

          .badge {
            padding: 6px 12px;
            border-radius: 16px;
            font-size: 13px;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            gap: 6px;

            &.badge-success {
              background-color: #d4edda;
              color: #155724;

              i {
                font-size: 14px;
              }
            }

            &.badge-processing {
              background-color: #fff3cd;
              color: #856404;
            }

            &.badge-error {
              background-color: #f8d7da;
              color: #721c24;
            }
          }

          .video-link {
            color: var(--primary, #422648);
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            transition: all 0.2s;

            &:hover {
              text-decoration: underline;
              color: var(--primaryDark, #2e1a30);
            }

            i {
              font-size: 14px;
            }
          }

          .btn-copy {
            background-color: var(--primary, #422648);
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 13px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s;

            &:hover {
              background-color: var(--primaryDark, #2e1a30);
              transform: translateY(-1px);
            }

            i {
              font-size: 13px;
            }
          }
        }
      }
    }
  }

  .upload-error {
    padding: 24px;

    .error-icon {
      i {
        font-size: 64px;
        color: #dc3545;
      }
    }

    h3 {
      color: #dc3545;
      font-size: 24px;
      margin: 16px 0;
    }

    p {
      color: var(--semantic-neutral-700, #495057);
      font-size: 16px;
      margin: 12px 0 24px 0;
    }

    .error-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
    }
  }

  h3 {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 20px;
    margin-bottom: 20px;
    color: var(--semantic-neutral-800, #212529);

    i {
      font-size: 24px;
    }
  }
}
</style>
